import React, {
  useContext,
  useCallback,
  createContext,
  useState,
  useEffect,
  useLayoutEffect,
} from 'react'
import { useAlert } from './AlertModal'
import { getApi, updateCsrfToken } from '../../../util/api'
import useTranslation from '../../shared/TranslationProvider'
import useTheme from '../../shared/ThemeProvider'

const DataContext = createContext()

export const useData = () => {
  return useContext(DataContext)
}

export const DataProvider = ({
  initialFormState,
  inviteCode,
  googleAuthEnabled,
  appleAuthEnabled,
  panelAutoRotationDisabled,
  userNotificationEnabled,
  flashAlert,
  flashNotice,
  appleOauthAttachEmail,
  googleOauthAttachEmail,
  legalNotice,
  newLegalNotice,
  children,
}) => {
  const { setAlertData, setAlertState, clearAlert } = useAlert()
  const { t, locale } = useTranslation()
  const { platformName } = useTheme()

  const [formState, _setFormState] = useState(initialFormState)
  const [invitation, setInvitation] = useState({})
  const [otpCreateData, setOtpCreateData] = useState({})
  const [existingUsername, setExistingUsername] = useState('')
  const [existingUserApple, setExistingUserApple] = useState(false)
  const [existingUserGoogle, setExistingUserGoogle] = useState(false)
  const [programLevel, setProgramLevel] = useState('')
  const [webinars, setWebinars] = useState([])
  const [disableRotation, setDisableRotation] = useState(
    panelAutoRotationDisabled,
  )
  const [isLoading, setIsLoading] = useState(false)
  const [documentTitle, setDocumentTitle] = useState('')

  // CTS-9309 (YNS-017): History API — replaceState for initial entry, pushState on each step,
  // popstate restores formState; applyFormStateFromResponse skips a push when state is unchanged
  // (e.g. invite URL flow already pushed RegistrationForm).
  const pushFormStateUrl = () =>
    `${window.location.pathname}${window.location.search}${window.location.hash || ''}`

  const setFormState = useCallback((newState) => {
    if (typeof window !== 'undefined' && window.history?.pushState) {
      window.history.pushState({ formState: newState }, '', pushFormStateUrl())
    }
    _setFormState(newState)
  }, [])

  const applyFormStateFromResponse = useCallback((newState) => {
    _setFormState((prev) => {
      if (prev === newState) return prev
      if (typeof window !== 'undefined' && window.history?.pushState) {
        window.history.pushState(
          { formState: newState },
          '',
          pushFormStateUrl(),
        )
      }
      return newState
    })
  }, [])

  useLayoutEffect(() => {
    if (typeof window === 'undefined' || !window.history?.replaceState) return
    const url = `${window.location.pathname}${window.location.search}${window.location.hash || ''}`
    window.history.replaceState({ formState: initialFormState }, '', url)
  }, [initialFormState])

  useEffect(() => {
    const handlePopState = (event) => {
      if (event.state && typeof event.state.formState === 'string') {
        _setFormState(event.state.formState)
      } else {
        _setFormState(initialFormState)
      }
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [initialFormState])

  const oauthEnabled = googleAuthEnabled || appleAuthEnabled

  const handleFormResponse = useCallback(
    (response) => {
      updateCsrfToken(response.data.csrf_token)
      if (response.data.location) {
        setIsLoading(true)
        window.location.href = response.data.location
      }
      if (response.data.valid_invitation) {
        setInvitation(response.data.valid_invitation)
        setProgramLevel(response.data.valid_invitation.program_level)
      }
      if (response.data.webinars) {
        setWebinars(response.data.webinars)
      }
      if (response.data.existing_username) {
        setExistingUsername(response.data.existing_username)
      }
      if (response.data.existing_user_apple) {
        setExistingUserApple(response.data.existing_user_apple)
      }
      if (response.data.existing_user_google) {
        setExistingUserGoogle(response.data.existing_user_google)
      }
      if (response.data.otp_create_data) {
        setOtpCreateData(response.data.otp_create_data)
      }
      if (response.data.react_form_state) {
        setIsLoading(false)
        applyFormStateFromResponse(response.data.react_form_state)
      }
      if (response.data.previous_state === 'ForgotPassword') {
        setAlertState('success')
        setAlertData(response.data.notice)
      }
    },
    [setAlertData, setAlertState, applyFormStateFromResponse],
  )

  const getInvitation = useCallback(
    async (invitationCode, source = null) => {
      try {
        const response = await getApi().get('/users/sign_up', {
          params: {
            invitation: invitationCode.trim(),
            locale: locale,
            source: source,
          },
        })
        setIsLoading(false)
        if (response.data.alert) {
          setAlertData(response.data.alert)
          setAlertState('alert')
        } else if (response.data.notice) {
          setAlertData(response.data.notice)
          setAlertState('notice')
        } else if (response.data.valid_invitation) {
          clearAlert()
        }
        handleFormResponse(response)
      } catch {
        setIsLoading(false)
        setAlertData(t('code_validation_error'))
        setAlertState('alert')
      }
    },
    [setAlertData, setAlertState, clearAlert, locale, handleFormResponse, t],
  )

  useEffect(() => {
    if (inviteCode && inviteCode.length > 0) {
      // CTS-9309 (YNS-017): user-facing step change — recorded in history for back/forward
      setFormState('RegistrationForm')
      getInvitation(inviteCode)
    } else if (localStorage.getItem('unsubscribed')) {
      setAlertState('success')
      setAlertData(t('unsubscribe_success'))
      localStorage.removeItem('unsubscribed')
    }
  }, [inviteCode, getInvitation, setAlertData, setAlertState, setFormState, t])

  useEffect(() => {
    setDocumentTitle(document.title)
  }, [setDocumentTitle])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [formState])

  useEffect(() => {
    if (
      ['LoginForm', 'TwoFactorEntry', 'TwoFactorCreate'].includes(formState)
    ) {
      document.title = `${t('LoginForm.log_in_button')} | ${platformName}`
    }
    if (formState === 'InviteCodeEntry') {
      document.title = `${t('InviteCodeEntry.validate_code')} | ${platformName}`
    }
    if (
      ['RegistrationForm', 'AddLicense', 'AccountServices'].includes(formState)
    ) {
      document.title = `${t(
        'RegistrationForm.create_button',
      )} | ${platformName}`
    }
    if (formState === 'ResetPassword') {
      document.title = `${t('ResetPassword.reset_password')} | ${platformName}`
    }
    if (formState === 'ForgotPassword') {
      document.title = `${t('forgot_password')} | ${platformName}`
    }
  }, [formState, documentTitle, t, platformName])

  const data = {
    formState,
    invitation,
    otpCreateData,
    existingUsername,
    existingUserApple,
    existingUserGoogle,
    programLevel,
    webinars,
    oauthEnabled,
    googleAuthEnabled,
    appleAuthEnabled,
    disableRotation,
    userNotificationEnabled,
    flashAlert,
    flashNotice,
    isLoading,
    setFormState,
    setOtpCreateData,
    setExistingUsername,
    setDisableRotation,
    setIsLoading,
    handleFormResponse,
    getApi,
    getInvitation,
    appleOauthAttachEmail,
    googleOauthAttachEmail,
    legalNotice,
    newLegalNotice,
  }
  return <DataContext.Provider value={data}>{children}</DataContext.Provider>
}
